import React from 'react';
import { motion } from 'motion/react';
import { Shield, ArrowLeft, Lock, Eye, FileText, Database, UserCheck, Globe, Bell, Mail, ExternalLink, CheckCircle, Gavel, ShieldAlert, Scale, Clock, Briefcase, Zap, Info, Landmark, ShieldCheck } from 'lucide-react';

interface PrivacyPolicyProps {
  onBack?: () => void;
}

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
  return (
    <div className="bg-[#05050a] min-h-screen text-slate-100 font-sans selection:bg-indigo-500/30 overflow-y-auto custom-scrollbar relative z-10">
      {/* Background Decor */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-indigo-500/10 blur-[120px] rounded-full animate-pulse" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-purple-500/10 blur-[150px] rounded-full" />
        <div className="absolute top-[30%] left-[20%] w-[1px] h-[40%] bg-gradient-to-b from-transparent via-indigo-500/20 to-transparent" />
      </div>

      <div className="max-w-5xl mx-auto px-6 py-20 relative z-10">
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={onBack || (() => window.history.back())}
          className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors mb-12 group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-bold uppercase tracking-wider text-sm">Back to Experience</span>
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="relative inline-block mb-16">
            <div className="flex items-center gap-6">
              <div className="p-4 bg-indigo-600/10 rounded-2xl border border-indigo-500/20 relative group">
                <div className="absolute -inset-1 bg-indigo-500/20 blur-xl rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                <Shield className="w-12 h-12 text-indigo-400 relative z-10" />
              </div>
              <div>
                <h1 className="text-5xl sm:text-7xl font-black tracking-tighter text-white uppercase italic drop-shadow-[0_0_20px_rgba(255,255,255,0.1)]">
                  Privacy Policy <span className="text-indigo-400">& Terms</span>
                </h1>
                <div className="flex items-center gap-2 mt-3">
                  <div className="h-[2px] w-12 bg-indigo-500 rounded-full" />
                  <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Comprehensive Privacy Policy & Terms of Service • August 2026</p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Details Metadata Card */}
          <div className="mb-12 p-6 bg-slate-900/60 border border-indigo-500/20 rounded-3xl backdrop-blur-xl grid sm:grid-cols-2 md:grid-cols-4 gap-4 text-xs font-mono">
            <div>
              <span className="text-slate-500 uppercase tracking-wider block mb-1">Effective Date</span>
              <span className="text-indigo-300 font-bold">August 2026</span>
            </div>
            <div>
              <span className="text-slate-500 uppercase tracking-wider block mb-1">Entity</span>
              <span className="text-white font-bold">Professor X AI Labs</span>
            </div>
            <div>
              <span className="text-slate-500 uppercase tracking-wider block mb-1">Contact Email</span>
              <a href="mailto:professorxai108@gmail.com" className="text-indigo-400 hover:underline font-bold">professorxai108@gmail.com</a>
            </div>
            <div>
              <span className="text-slate-500 uppercase tracking-wider block mb-1">Application Homepage</span>
              <a href="https://professorxai.info/" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline font-bold flex items-center gap-1">
                professorxai.info <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* PART 1: PRIVACY POLICY HEADER */}
          <div className="mb-8 p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-2xl flex items-center gap-3">
            <Shield className="w-6 h-6 text-indigo-400 shrink-0" />
            <h2 className="text-2xl font-black text-white uppercase italic tracking-wide">Part I: 🛡️ Privacy Policy</h2>
          </div>

          <div className="space-y-16 text-slate-400 leading-relaxed max-w-4xl">
            {/* 01. Operational Overview & Privacy Philosophy */}
            <section className="relative group">
              <div className="absolute -inset-6 bg-gradient-to-r from-indigo-500/5 to-purple-500/5 rounded-[3rem] blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative bg-white/[0.02] border border-white/5 p-12 rounded-[2.5rem] backdrop-blur-2xl">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20 shadow-inner">
                    <Lock className="w-6 h-6 text-indigo-400" />
                  </div>
                  <h2 className="text-3xl font-black text-white uppercase tracking-tight italic">01. Operational Overview & Privacy Philosophy</h2>
                </div>
                <p className="text-xl font-medium text-slate-300">
                  Professor X AI Labs ("Professor X", "we", "us", or "our") operates an interactive, visual AI educational platform. We are committed to maintaining a strict Privacy-First architecture. Instructional inquiry and academic curiosity are treated as private and confidential. Our application is engineered to facilitate AI-driven learning without persistent surveillance, unauthorized profiling, or commodification of user data.
                </p>
              </div>
            </section>

            {/* 02. Google User Data & OAuth Permissions */}
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <UserCheck className="w-6 h-6 text-indigo-400" />
                <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">02. Google User Data & OAuth Permissions</h2>
              </div>
              <p className="text-slate-300">
                When you log in to Professor X using your Google Account via OAuth 2.0, our application requests access to specific scopes to authenticate your identity and authorize real-time generative AI interactions.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-indigo-500/30 transition-all group/card">
                  <h3 className="text-white font-black uppercase tracking-wide mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-indigo-400" /> A. Scopes Requested & Access Details
                  </h3>
                  <ul className="space-y-3 text-sm">
                    <li>
                      <strong className="text-indigo-300 block">Identity Scopes (<code className="font-mono text-xs">openid</code>, <code className="font-mono text-xs">userinfo.email</code>, <code className="font-mono text-xs">userinfo.profile</code>):</strong> We access your primary Google email address, display name, and profile URI to initialize your secure session and manage quota validation.
                    </li>
                    <li>
                      <strong className="text-indigo-300 block">AI Integration & Processing:</strong> Educational queries, visual lessons, and diagrams are generated via secure server-side Gemini processing or your provided Gemini API key. We do not access, sell, or train external models on your private Google profile data.
                    </li>
                  </ul>
                </div>
                <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-indigo-500/30 transition-all group/card">
                  <h3 className="text-white font-black uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Eye className="w-4 h-4 text-indigo-400" /> B. Purpose of Access
                  </h3>
                  <p className="text-sm text-slate-300">
                    Google user data is accessed solely to provide, maintain, and improve prominent user-facing features within the Professor X interface—specifically, executing user-submitted queries and displaying generated visual and step-by-step explanations.
                  </p>
                </div>
              </div>
            </section>

            {/* 03. Google User Data Sharing, Transfers & Disclosures */}
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <Shield className="w-6 h-6 text-indigo-400" />
                <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">03. Google User Data Sharing, Transfers & Disclosures</h2>
              </div>
              <p className="text-slate-300">
                We maintain absolute transparency regarding how Google user data is handled.
              </p>
              <div className="space-y-4">
                <div className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-indigo-500/30 transition-all">
                  <h3 className="text-white font-bold uppercase tracking-wide mb-2 flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-indigo-400" /> No Data Selling or Commercial Exploitation
                  </h3>
                  <p className="text-sm">We do <strong>not</strong> sell, rent, trade, or lease Google user data or personal user information to any third party for monetary or other valuable consideration.</p>
                </div>
                <div className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-indigo-500/30 transition-all">
                  <h3 className="text-white font-bold uppercase tracking-wide mb-2 flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-indigo-400" /> No Third-Party Brokers or Advertisers
                  </h3>
                  <p className="text-sm">Google user data is <strong>never</strong> transferred, shared, or disclosed to advertising networks, retargeting services, data brokers, or commercial marketing platforms.</p>
                </div>
                <div className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-indigo-500/30 transition-all">
                  <h3 className="text-white font-bold uppercase tracking-wide mb-2 flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-indigo-400" /> Limited Service Transfers
                  </h3>
                  <p className="text-sm">Google user data and session context are transferred <strong>exclusively</strong> to Google API endpoints (<code className="font-mono text-xs text-indigo-300">generativelanguage.googleapis.com</code>) to execute AI generation requests explicitly initiated by the user.</p>
                </div>
                <div className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-indigo-500/30 transition-all">
                  <h3 className="text-white font-bold uppercase tracking-wide mb-2 flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-indigo-400" /> No AI/ML Model Training
                  </h3>
                  <p className="text-sm">User data, conversation histories, prompt inputs, and OAuth token data obtained through Google APIs are <strong>never</strong> used to train, retune, or improve generalized or non-personalized machine learning (ML) or artificial intelligence (AI) models.</p>
                </div>
                <div className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-indigo-500/30 transition-all">
                  <h3 className="text-white font-bold uppercase tracking-wide mb-2 flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-indigo-400" /> Human Readability Restrictions
                  </h3>
                  <p className="text-sm">Human personnel are strictly prohibited from reading or viewing Google user data, conversation logs, or private tokens. Human review occurs only in rare, legally required exceptions (such as investigating security abuse or complying with enforceable law enforcement subpoenas).</p>
                </div>
              </div>
            </section>

            {/* 04. Google API Services User Data Policy Compliance */}
            <section className="space-y-6">
              <div className="bg-gradient-to-r from-indigo-900/20 via-slate-900/50 to-indigo-900/20 border border-indigo-500/30 p-10 rounded-[2.5rem] relative overflow-hidden">
                <div className="flex items-center gap-3 mb-4">
                  <Globe className="w-6 h-6 text-indigo-400" />
                  <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">04. Google API Services User Data Policy Compliance (Limited Use Requirement)</h2>
                </div>
                <p className="text-base text-slate-300">
                  Professor X’s use and transfer to any other app of information received from Google APIs will adhere to the{' '}
                  <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline font-bold inline-flex items-center gap-1">
                    Google API Services User Data Policy <ExternalLink className="w-4 h-4" />
                  </a>
                  , including the <strong>Limited Use requirements</strong>.
                </p>
              </div>
            </section>

            {/* 05. Technical Storage, Encryption & Security Standards */}
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <Database className="w-6 h-6 text-indigo-400" />
                <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">05. Technical Storage, Encryption & Security Standards</h2>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-indigo-500/30 transition-all">
                  <h3 className="text-white font-black uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-indigo-400" /> Data in Transit
                  </h3>
                  <p className="text-sm">All network communication between your web browser, our application, and Google API servers is protected using <strong>TLS 1.3</strong> high-entropy encryption protocol.</p>
                </div>
                <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-indigo-500/30 transition-all">
                  <h3 className="text-white font-black uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Lock className="w-4 h-4 text-indigo-400" /> Data at Rest
                  </h3>
                  <p className="text-sm">Cryptographic secrets and short-lived OAuth tokens are encrypted at rest using industry-accepted security standards. Client-side configuration settings are stored locally in the user's browser storage (<code className="font-mono text-xs text-indigo-300">localStorage</code>) and sandboxed from unauthorized server telemetry.</p>
                </div>
                <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-indigo-500/30 transition-all">
                  <h3 className="text-white font-black uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Bell className="w-4 h-4 text-indigo-400" /> Ephemeral Retention
                  </h3>
                  <p className="text-sm">Session telemetry, mathematical queries, and uploaded images are processed ephemerally. Server memory retains prompt context for roughly 24 hours solely to support continuous follow-up questions within an active session, after which memory logs are systematically overwritten.</p>
                </div>
              </div>
            </section>

            {/* 06. Global Compliance & User Rights */}
            <section className="space-y-8">
              <div className="flex items-center gap-3">
                <Bell className="w-6 h-6 text-indigo-400" />
                <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">06. Global Compliance & User Rights</h2>
              </div>
              <div className="bg-indigo-900/10 border border-indigo-500/20 p-10 rounded-[2.5rem] relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 blur-3xl rounded-full translate-x-16 -translate-y-16" />
                <div className="relative pr-8 space-y-8">
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-white font-bold uppercase text-xs tracking-widest mb-2">A. EEA / UK (GDPR) Rights</h4>
                      <p className="text-xs mb-3">If you reside in the European Economic Area or United Kingdom, you possess the following statutory rights:</p>
                      <ul className="text-xs space-y-2 pl-4 list-disc text-slate-300">
                        <li><strong>Right to Erasure ("Right to be Forgotten"):</strong> You can immediately terminate your session data by clearing your browser’s <code className="font-mono text-[10px] text-indigo-300">localStorage</code> and logging out of Professor X.</li>
                        <li><strong>Right to Access & Portability:</strong> You may request details on personal identity attributes maintained by contacting <a href="mailto:professorxai108@gmail.com" className="text-indigo-400 hover:underline">professorxai108@gmail.com</a>.</li>
                      </ul>
                    </div>

                    <div className="border-t border-indigo-500/20 pt-6">
                      <h4 className="text-white font-bold uppercase text-xs tracking-widest mb-2">B. California Residents (CCPA / CPRA)</h4>
                      <p className="text-xs text-slate-300">We do not "sell" or "share" personal information as defined under the California Consumer Privacy Act. Your data is processed exclusively to deliver requested educational services.</p>
                    </div>

                    <div className="border-t border-indigo-500/20 pt-6">
                      <h4 className="text-white font-bold uppercase text-xs tracking-widest mb-2">C. Children’s Privacy</h4>
                      <p className="text-xs text-slate-300">Professor X is not directed at children under 13 years of age. In compliance with COPPA, we do not knowingly collect or solicit personal information or Google user data from children under 13. If we discover that a child under 13 has provided personal information, we immediately purge the data from active systems.</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* 07. Updates to This Privacy Policy */}
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-indigo-400" />
                <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">07. Updates to This Privacy Policy</h2>
              </div>
              <p className="text-slate-300">
                We may update this Privacy Policy to reflect technical or legal changes. Any material updates will be published directly on this page (<code className="font-mono text-indigo-300 text-sm">https://professorxai.info/privacy</code>) with a revised Effective Date. Continued use of Professor X following an update constitutes acceptance of the revised terms.
              </p>
            </section>

            {/* SEPARATOR / PART 2: TERMS OF SERVICE HEADER */}
            <div className="my-16 pt-16 border-t border-purple-500/30">
              <div className="mb-8 p-4 bg-purple-500/10 border border-purple-500/30 rounded-2xl flex items-center gap-3">
                <Gavel className="w-6 h-6 text-purple-400 shrink-0" />
                <h2 className="text-2xl font-black text-white uppercase italic tracking-wide">Part II: ⚖️ Terms of Service</h2>
              </div>

              <div className="space-y-16 text-slate-400 leading-relaxed max-w-4xl">
                {/* 01. Acceptance of Terms & Algorithmic Framework */}
                <section className="relative group">
                  <div className="absolute -inset-6 bg-gradient-to-r from-purple-500/5 to-indigo-500/5 rounded-[3rem] blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="relative bg-white/[0.02] border border-white/5 p-12 rounded-[2.5rem] backdrop-blur-2xl">
                    <div className="flex items-center gap-4 mb-8">
                      <div className="w-12 h-12 rounded-2xl bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                        <Globe className="w-6 h-6 text-purple-400" />
                      </div>
                      <h2 className="text-3xl font-black text-white uppercase tracking-tight italic">01. Acceptance of Terms & Algorithmic Framework</h2>
                    </div>
                    <p className="text-xl font-medium text-slate-300">
                      By initializing an account session or interacting with Professor X (<code className="font-mono text-purple-300 text-lg">https://professorxai.info/</code>), you enter into a legally binding contract with Professor X AI Labs. This framework governs your access to our visual tools, algorithmic pedagogical explanations, and AI integrations. If you do not agree to every clause herein, you must immediately terminate your interaction with the platform.
                    </p>
                  </div>
                </section>

                {/* 02. Usage Parameters & Intellectual Property */}
                <section className="space-y-6">
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-6 h-6 text-purple-400" />
                    <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">02. Usage Parameters & Intellectual Property</h2>
                  </div>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all group/card">
                      <h3 className="text-white font-black uppercase tracking-wide mb-3 flex items-center gap-2 text-sm">
                        <Zap className="w-4 h-4 text-purple-400" /> Revocable Educational License
                      </h3>
                      <p className="text-sm">You are granted a limited, non-exclusive, non-transferable, revocable license to use Professor X for personal, non-commercial educational advancement.</p>
                    </div>
                    <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all group/card">
                      <h3 className="text-white font-black uppercase tracking-wide mb-3 flex items-center gap-2 text-sm">
                        <ShieldAlert className="w-4 h-4 text-purple-400" /> Commercial Restrictions
                      </h3>
                      <p className="text-sm">Systematic scraping, commercial redistribution, resale, or sublicensing of our platform outputs or user interface assets is strictly prohibited.</p>
                    </div>
                    <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all group/card">
                      <h3 className="text-white font-black uppercase tracking-wide mb-3 flex items-center gap-2 text-sm">
                        <Scale className="w-4 h-4 text-purple-400" /> Academic Integrity Policy
                      </h3>
                      <p className="text-sm">Professor X is engineered as a collaborative visual learning aid. You agree <strong>not</strong> to misuse the service to engage in academic dishonesty, violate institutional codes of conduct, cheat on proctored examinations, or generate fraudulent academic submissions.</p>
                    </div>
                  </div>
                </section>

                {/* 03. Google API & OAuth Usage Terms */}
                <section className="space-y-6">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="w-6 h-6 text-purple-400" />
                    <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">03. Google API & OAuth Usage Terms</h2>
                  </div>
                  <p className="text-slate-300">
                    By logging in via Google OAuth to use Professor X, you acknowledge and agree that:
                  </p>
                  <div className="space-y-4">
                    <div className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all flex items-start gap-4">
                      <span className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-300 font-bold flex items-center justify-center shrink-0 text-sm">1</span>
                      <p className="text-sm pt-1">You grant Professor X authorization to issue Generative Language API requests using the temporary OAuth tokens generated during your session.</p>
                    </div>
                    <div className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all flex items-start gap-4">
                      <span className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-300 font-bold flex items-center justify-center shrink-0 text-sm">2</span>
                      <p className="text-sm pt-1">
                        Your interactions with generative AI endpoints are subject to{' '}
                        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline font-bold inline-flex items-center gap-1">
                          Google's Terms of Service <ExternalLink className="w-3 h-3" />
                        </a>{' '}
                        and{' '}
                        <a href="https://policies.google.com/terms/generative-ai" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline font-bold inline-flex items-center gap-1">
                          Generative AI Additional Terms of Service <ExternalLink className="w-3 h-3" />
                        </a>.
                      </p>
                    </div>
                    <div className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all flex items-start gap-4">
                      <span className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-300 font-bold flex items-center justify-center shrink-0 text-sm">3</span>
                      <p className="text-sm pt-1">You will not attempt to exploit OAuth tokens, forge client credentials, or bypass Google API quota limits.</p>
                    </div>
                  </div>
                </section>

                {/* 04. Prohibited Conduct */}
                <section className="space-y-6">
                  <div className="flex items-center gap-3">
                    <ShieldAlert className="w-6 h-6 text-purple-400" />
                    <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">04. Prohibited Conduct</h2>
                  </div>
                  <p className="text-slate-300">When accessing Professor X, you explicitly agree <strong>not</strong> to:</p>
                  <ul className="grid sm:grid-cols-2 gap-4 text-sm">
                    <li className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-purple-400 mt-2 shrink-0" />
                      <span>Decompile, reverse-engineer, or attempt to extract source code or underlying parameters from the platform or its API endpoints.</span>
                    </li>
                    <li className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-purple-400 mt-2 shrink-0" />
                      <span>Upload malformed scripts, viruses, or malicious payloads designed to disrupt service availability.</span>
                    </li>
                    <li className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-purple-400 mt-2 shrink-0" />
                      <span>Automate query submissions via unapproved web scrapers, bots, or automated extraction tools.</span>
                    </li>
                    <li className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-purple-400 mt-2 shrink-0" />
                      <span>Use the platform to generate unlawful, harassing, defamatory, or dangerous content.</span>
                    </li>
                  </ul>
                </section>

                {/* 05. Risk Allocations & Disclaimers */}
                <section className="space-y-6">
                  <div className="flex items-center gap-3">
                    <Info className="w-6 h-6 text-purple-400" />
                    <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">05. Risk Allocations & Disclaimers</h2>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all">
                      <h3 className="text-white font-black uppercase tracking-wide mb-3 flex items-center gap-2">
                        <Info className="w-4 h-4 text-purple-400" /> Accuracy Disclaimer
                      </h3>
                      <p className="text-sm">Generative AI models operate probabilistically and may occasionally generate inaccurate mathematical, scientific, or factual information ("hallucinations"). Instructional content is provided on an <strong>"AS IS"</strong> and <strong>"AS AVAILABLE"</strong> basis without guarantees of academic performance, certification pass rates, or specific test scores.</p>
                    </div>
                    <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all">
                      <h3 className="text-white font-black uppercase tracking-wide mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-purple-400" /> Service Availability & Latency
                      </h3>
                      <p className="text-sm">Platform functionality relies on third-party cloud infrastructure, including Google Cloud and the Generative Language API. Professor X AI Labs is not liable for service interruptions, latency, rate limits, or outages originating from external network nodes.</p>
                    </div>
                  </div>
                </section>

                {/* 06. Limitation of Liability */}
                <section className="space-y-6">
                  <div className="flex items-center gap-3">
                    <Scale className="w-6 h-6 text-purple-400" />
                    <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">06. Limitation of Liability</h2>
                  </div>
                  <div className="p-8 bg-slate-900/40 rounded-3xl border border-white/5 hover:border-purple-500/30 transition-all">
                    <p className="text-slate-300 text-sm leading-relaxed">
                      To the maximum extent permitted by applicable law, in no event shall Professor X AI Labs, its developers, founders, or affiliates be liable for any indirect, incidental, punitive, special, or consequential damages arising out of or in connection with your use or inability to use the platform.
                    </p>
                  </div>
                </section>

                {/* 07. Statutory Framework & Dispute Resolution */}
                <section className="space-y-8">
                  <div className="flex items-center gap-3">
                    <Landmark className="w-6 h-6 text-purple-400" />
                    <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">07. Statutory Framework & Dispute Resolution</h2>
                  </div>
                  <div className="bg-purple-900/10 border border-purple-500/20 p-10 rounded-[2.5rem] relative overflow-hidden group">
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-500/10 blur-3xl rounded-full -translate-x-16 translate-y-16" />
                    <div className="relative pl-8 space-y-8 border-l-2 border-purple-500/30">
                      <div className="space-y-2">
                        <h4 className="text-white font-black uppercase tracking-tighter text-sm">Individual Binding Arbitration</h4>
                        <p className="text-sm text-slate-300">You agree that any dispute, claim, or controversy arising out of these Terms shall be settled through binding individual arbitration under commercial arbitration standards, rather than in court.</p>
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-white font-black uppercase tracking-tighter text-sm">Class Action Waiver</h4>
                        <p className="text-sm text-slate-300">You explicitly waive the right to initiate, join, or participate as a plaintiff or class member in any class action, collective lawsuit, or representative proceeding against Professor X AI Labs.</p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* 08. Contact Information */}
                <section className="space-y-6">
                  <div className="flex items-center gap-3">
                    <Mail className="w-6 h-6 text-purple-400" />
                    <h2 className="text-2xl font-bold text-white uppercase tracking-tight italic">08. Contact Information</h2>
                  </div>
                  <p className="text-slate-300">
                    If you have legal questions, security inquiries, or privacy requests regarding these documents, contact us at:
                  </p>
                  <div className="p-6 bg-slate-900/40 rounded-3xl border border-white/5 flex flex-col sm:flex-row gap-6 text-sm">
                    <div>
                      <span className="text-slate-500 block mb-1">📧 Email</span>
                      <a href="mailto:professorxai108@gmail.com" className="text-purple-400 font-bold hover:underline font-mono">professorxai108@gmail.com</a>
                    </div>
                    <div>
                      <span className="text-slate-500 block mb-1">🌐 Website</span>
                      <a href="https://professorxai.info/" target="_blank" rel="noopener noreferrer" className="text-purple-400 font-bold hover:underline font-mono flex items-center gap-1">
                        https://professorxai.info/ <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </section>
              </div>
            </div>

            <section className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center gap-3 mb-2 justify-center md:justify-start">
                  <Mail className="w-5 h-5 text-indigo-400" />
                  <h3 className="text-xl font-bold text-white uppercase italic">Contact Support & Legal</h3>
                </div>
                <p className="text-sm">Questions regarding privacy or legal terms? Contact our team at:</p>
                <a href="mailto:professorxai108@gmail.com" className="text-indigo-400 font-bold hover:underline font-mono">professorxai108@gmail.com</a>
              </div>
              <button 
                onClick={onBack || (() => window.history.back())}
                className="px-12 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl font-black uppercase tracking-widest transition-all shadow-xl shadow-indigo-500/20 hover:scale-105 active:scale-95"
              >
                Accept Privacy & Terms
              </button>
            </section>
          </div>
        </motion.div>

        <footer className="mt-32 pt-12 border-t border-white/5 text-center">
          <p className="text-slate-600 text-xs font-bold uppercase tracking-widest leading-loose">
            &copy; 2026 Professor X AI Labs • All Rights Reserved<br/>
            Engineered for Academic Freedom & Universal Access
          </p>
        </footer>
      </div>
    </div>
  );
};

